import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Trash2, X, Plus } from "lucide-react";

const empty = {
    title: "",
    location: "",
    type: "Vollzeit",
    salary_range: "",
    description: "",
    tags: [],
    is_published: true,
};

export default function JobFormModal({ job, open, onOpenChange, onSaved }) {
    const [form, setForm] = useState(empty);
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const isEdit = !!job?.id;

    useEffect(() => {
        if (job) {
            setForm({
                title: job.title || "",
                location: job.location || "",
                type: job.type || "Vollzeit",
                salary_range: job.salary_range || "",
                description: job.description || "",
                tags: job.tags || [],
                is_published: job.is_published !== false,
            });
        } else {
            setForm(empty);
        }
        setConfirmDelete(false);
        setTagInput("");
    }, [job, open]);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const addTag = () => {
        const t = tagInput.trim();
        if (!t) return;
        if (form.tags.includes(t)) return;
        setForm((f) => ({ ...f, tags: [...f.tags, t] }));
        setTagInput("");
    };

    const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

    const save = async () => {
        if (!form.title || !form.location) {
            toast.error("Titel und Standort sind Pflicht");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/admin/jobs/${job.id}`, form);
                toast.success("Stelle aktualisiert");
            } else {
                await api.post("/admin/jobs", form);
                toast.success("Stelle angelegt");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setSaving(true);
        try {
            await api.delete(`/admin/jobs/${job.id}`);
            toast.success("Stelle gelöscht");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="job-form-modal"
                className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0"
            >
                <div className="bg-aimploy-navy text-white p-6 lg:p-8 rounded-t-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl lg:text-3xl font-bold text-white">
                            {isEdit ? "Stelle bearbeiten" : "Neue Stelle anlegen"}
                        </DialogTitle>
                        <DialogDescription className="text-white/70 mt-1">
                            {isEdit ? "Anpassen, archivieren oder löschen." : "Erscheint sofort auf der Kandidaten-Seite."}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="p-6 lg:p-8 space-y-5">
                    <Field label="Titel *">
                        <input
                            name="title"
                            value={form.title}
                            onChange={onChange}
                            placeholder="z.B. Senior Frontend Engineer"
                            data-testid="job-form-title"
                            className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm"
                        />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Standort *">
                            <input
                                name="location"
                                value={form.location}
                                onChange={onChange}
                                placeholder="Berlin / Remote"
                                data-testid="job-form-location"
                                className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm"
                            />
                        </Field>
                        <Field label="Beschäftigung">
                            <input
                                name="type"
                                value={form.type}
                                onChange={onChange}
                                placeholder="Vollzeit"
                                className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm"
                            />
                        </Field>
                    </div>

                    <Field label="Gehaltsrange">
                        <input
                            name="salary_range"
                            value={form.salary_range}
                            onChange={onChange}
                            placeholder="85.000 € – 110.000 €"
                            className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm"
                        />
                    </Field>

                    <Field label="Beschreibung">
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={onChange}
                            rows={4}
                            placeholder="Worum geht es bei dieser Rolle?"
                            className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm resize-none"
                        />
                    </Field>

                    <Field label="Tags">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {form.tags.map((t) => (
                                <span key={t} className="inline-flex items-center gap-1.5 bg-aimploy-violet/10 text-aimploy-violet text-xs font-semibold px-3 py-1.5 rounded-full">
                                    {t}
                                    <button onClick={() => removeTag(t)} aria-label="Tag entfernen">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addTag();
                                    }
                                }}
                                placeholder="Tag hinzufügen + Enter"
                                className="flex-1 border border-aimploy-navy/15 rounded-xl px-4 py-2.5 text-sm"
                            />
                            <button
                                type="button"
                                onClick={addTag}
                                className="px-4 py-2.5 rounded-xl bg-aimploy-bg hover:bg-aimploy-violet/10 text-sm font-semibold flex items-center gap-1.5"
                            >
                                <Plus className="w-4 h-4" /> Add
                            </button>
                        </div>
                    </Field>

                    <Field label="Veröffentlicht">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, is_published: !f.is_published }))}
                                data-testid="job-form-publish-toggle"
                                className={`relative w-12 h-7 rounded-full transition-colors ${form.is_published ? "bg-aimploy-violet" : "bg-aimploy-navy/15"}`}
                                role="switch"
                                aria-checked={form.is_published}
                            >
                                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${form.is_published ? "translate-x-6" : "translate-x-1"}`} />
                            </button>
                            <span className="text-sm">{form.is_published ? "Sichtbar auf der Website" : "Entwurf / archiviert"}</span>
                        </label>
                    </Field>

                    <div className="flex gap-3 pt-2 border-t border-black/5 -mx-6 lg:-mx-8 px-6 lg:px-8 pt-6">
                        {isEdit && (
                            confirmDelete ? (
                                <button
                                    onClick={remove}
                                    disabled={saving}
                                    data-testid="job-form-delete-confirm"
                                    className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-xl text-sm font-semibold disabled:opacity-60"
                                >
                                    <Trash2 className="w-4 h-4" /> Wirklich löschen?
                                </button>
                            ) : (
                                <button
                                    onClick={() => setConfirmDelete(true)}
                                    data-testid="job-form-delete"
                                    className="inline-flex items-center justify-center gap-2 border border-red-300 text-red-500 hover:bg-red-50 px-4 py-3 rounded-xl text-sm font-semibold"
                                >
                                    <Trash2 className="w-4 h-4" /> Löschen
                                </button>
                            )
                        )}
                        <button
                            onClick={() => onOpenChange(false)}
                            className="flex-1 px-5 py-3 rounded-xl border border-aimploy-navy/15 text-sm font-semibold hover:bg-aimploy-bg"
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="job-form-save"
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-aimploy-navy text-white px-5 py-3 rounded-xl text-sm font-semibold hover:bg-aimploy-navy-hover disabled:opacity-60"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Speichern…" : isEdit ? "Aktualisieren" : "Anlegen"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">{label}</div>
            {children}
        </div>
    );
}
